import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import SmartImg from '../../components/ui/SmartImg';
import OwnersCard from '../../components/horse/OwnersCard';
import ProvidersCard from '../../components/horse/ProvidersCard';
import NotesTimeline from '../../components/horse/NotesTimeline';
import HorsePhotoGallery from '../../components/horse/HorsePhotoGallery';
import VetRecordsCard from '../../components/horse/VetRecordsCard';
import HorseSummary from '../../components/horse/HorseSummary';

export default function HorseDetail() {
  const { id } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['horse', id],
    queryFn: () => api.get(`/horses/${id}`),
  });

  // The same endpoint the list uses, narrowed to this horse. Its own request
  // rather than more fields on /horses/:id: the record loads and renders while
  // the aggregates are still running, and the two can fail independently — a
  // slow activity query shouldn't keep somebody from reading a policy number.
  const activityQ = useQuery({
    queryKey: ['horse-activity', id],
    queryFn: () => api.get(`/horses/activity?horseId=${id}`),
    retry: false,
  });

  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError || !data?.horse) {
    return (
      <div className="p-4">
        <p className="text-red-600">Horse not found.</p>
        <Link to="/horses" className="text-ink-800 underline">Back to horses</Link>
      </div>
    );
  }

  const h = data.horse;

  return (
    <div className="max-w-4xl mx-auto p-4">
      <Link to="/horses" className="text-sm text-ink-500 hover:text-ink-800">← Horses</Link>

      <header className="mt-2 mb-6 flex items-center gap-4">
        {h.photo_url ? (
          <SmartImg src={h.photo_url} width={80} className="w-20 h-20 rounded-full object-cover" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-ink-100" />
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold truncate">{h.name}</h1>
          {h.show_name && h.show_name !== h.name && (
            <p className="text-sm text-ink-500 truncate">Show name: {h.show_name}</p>
          )}
        </div>
        <Link to={`/horses/${id}/edit`} className="btn-primary shrink-0">Edit</Link>
      </header>

      {/* First, because it's the part that changed since you last looked.
          Everything below it — breed, height, policy number — stays true for
          months at a time. */}
      <HorseSummary
        snapshot={activityQ.data?.activity?.[id]}
        today={activityQ.data?.today}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <Section title="At the barn">
          <Row label="Stall" value={h.stall} />
          <Row label="Trainer" value={h.trainer_name} />
          <Row label="Description" value={h.description} />
        </Section>

        <Section title="Identity">
          <Row label="Breed" value={h.breed} />
          <Row label="Color" value={h.color} />
          <Row label="Markings" value={h.markings} />
          <Row label="Sex" value={h.sex} capitalize />
          <Row label="Date of birth" value={dobWithAge(h.birthdate)} />
          <Row label="Height" value={h.height_hh ? `${h.height_hh} hh` : null} />
          <Row label="Microchip" value={h.microchip} mono />
        </Section>

        <Section title="Registrations">
          <Row label="USEF #" value={h.usef_number} mono />
          <Row label="USHJA #" value={h.ushja_number} mono />
          <Row label="USDF #" value={h.usdf_number} mono />
        </Section>

        <Section title="Insurance">
          <Row label="Company" value={h.insurance_co} />
          <Row label="Policy" value={h.insurance_policy} mono />
          <Row label="Phone" value={h.insurance_phone} />
        </Section>
      </div>

      <HorsePhotoGallery horseId={id} />

      <OwnersCard horseId={id} owners={data.owners ?? []} />

      <ProvidersCard horseId={id} />

      <VetRecordsCard horseId={id} horse={h} owners={data.owners ?? []} />

      <NotesTimeline horseId={id} />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="card p-4">
      <h2 className="font-medium mb-3">{title}</h2>
      <dl className="space-y-2 text-sm">{children}</dl>
    </section>
  );
}

function Row({ label, value, mono, capitalize }) {
  const cls = [
    'text-right min-w-0 truncate',
    mono ? 'font-mono text-ink-800' : 'text-ink-800',
    capitalize ? 'capitalize' : '',
  ].join(' ');
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-500 shrink-0">{label}</dt>
      <dd className={cls}>
        {value || <span className="text-ink-300">—</span>}
      </dd>
    </div>
  );
}

// Show the stored date of birth plus a computed age, e.g. "2013-04-12 · Age 13".
// Falls back to the raw value if it isn't a parseable YYYY-MM-DD date.
function dobWithAge(birthdate) {
  if (!birthdate) return null;
  const age = ageFromBirthdate(birthdate);
  return age == null ? birthdate : `${birthdate} · Age ${age}`;
}

function ageFromBirthdate(birthdate) {
  const d = new Date(`${birthdate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 60 ? age : null;
}
